import { useEffect, useRef } from 'react';
import { usePrivyEmbeddedWallet } from '../usePrivyEmbeddedWallet';
import useWalletContext from '../../../useWalletContext';
import {
	usePrivyWalletMapping,
	useUpsertPrivyWalletMapping,
} from './usePrivyWalletMapping';

/**
 * Hook that automatically syncs Privy embedded wallet mappings to the database.
 *
 * This hook monitors for changes in the connected wallet and Privy embedded wallet.
 * When an embedded wallet is detected:
 * 1. Checks if a mapping already exists in the database
 * 2. If not, creates a new mapping
 * 3. If exists but different, updates the mapping
 *
 * This ensures that the database always has an up-to-date record of which
 * embedded wallets belong to which authorities.
 */
export const useSyncPrivyWalletMapping = () => {
	const walletContext = useWalletContext();
	const { embeddedWallet, solanaWalletAuthorityForPrivyUser } =
		usePrivyEmbeddedWallet();
	const { isPending: isUpserting, mutate: upsertMapping } =
		useUpsertPrivyWalletMapping();

	const authority = walletContext.wallet?.adapter?.publicKey;
	const authorityStr = authority?.toString() ?? null;
	const embeddedWalletStr = embeddedWallet?.address ?? null;

	// Track the last synced combination to avoid unnecessary API calls
	const lastSyncedRef = useRef<string | null>(null);

	const { data: existingMapping } = usePrivyWalletMapping(authorityStr);

	// Syncs the Privy embedded wallet to the database if it doesn't exist
	useEffect(() => {
		if (!authorityStr || !embeddedWalletStr) {
			return;
		}

		const syncKey = `${authorityStr}:${embeddedWalletStr}`;

		if (lastSyncedRef.current === syncKey) {
			return;
		}

		const needsUpsert =
			(!existingMapping ||
				existingMapping.embeddedWallet !== embeddedWalletStr) &&
			solanaWalletAuthorityForPrivyUser === authorityStr;

		if (needsUpsert && !isUpserting) {
			upsertMapping(
				{
					authority: authorityStr,
					embeddedWallet: embeddedWalletStr,
				},
				{
					onSuccess: () => {
						lastSyncedRef.current = syncKey;
					},
					onError: (error) => {
						console.error('Failed to sync Privy wallet mapping:', error);
					},
				}
			);
		} else if (!needsUpsert) {
			lastSyncedRef.current = syncKey;
		}
	}, [
		authorityStr,
		solanaWalletAuthorityForPrivyUser,
		embeddedWalletStr,
		existingMapping?.embeddedWallet,
		isUpserting,
		upsertMapping,
	]);

	// Reset the sync tracker when authority changes
	useEffect(() => {
		lastSyncedRef.current = null;
	}, [authorityStr]);
};
